import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import TrainingContentView from '../../components/training/TrainingContentView';
import {
  canManageOwnContent,
  teacherTrainingHostPath,
  teacherTrainingProgramEditPath,
} from '../../config/contentRoutes';
import { getTrainingHostBySlug, getTrainingProgramBySlug } from '../../utils/trainingHost';
import type { TrainingHost, TrainingProgram } from '../../types/trainingHost';

const statusLabel: Record<TrainingProgram['status'], string> = {
  completed: '완료',
  upcoming: '예정',
  active: '진행 중',
};

const TrainingProgramPublicPage: React.FC = () => {
  const { slug = '', programSlug = '' } = useParams<{ slug: string; programSlug: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const canManage = canManageOwnContent(currentUser?.role);

  const [host, setHost] = useState<TrainingHost | null>(null);
  const [program, setProgram] = useState<TrainingProgram | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const isOwner = !!host && !!currentUser && host.ownerUid === currentUser.uid;

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const hostData = await getTrainingHostBySlug(slug, currentUser?.uid);
        if (cancelled) return;
        if (!hostData) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        const programData = await getTrainingProgramBySlug(hostData.id, programSlug, currentUser?.uid);
        if (cancelled) return;

        const isEditorPage =
          programData?.contentType === 'editor' ||
          (!!programData?.contentHtml && programData?.contentType !== 'legacy');

        if (!programData || !isEditorPage) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        const isOwnProgram = programData.ownerUid === currentUser?.uid;
        if (!programData.isPublic && !isOwnProgram) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        setHost(hostData);
        setProgram(programData);
      } catch (err) {
        console.error(err);
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (slug && programSlug) load();
    return () => {
      cancelled = true;
    };
  }, [slug, programSlug, currentUser?.uid]);

  if (loading) {
    return (
      <div style={{ padding: '24px', fontFamily: 'var(--font-family-primary)' }}>
        <p style={{ color: 'var(--Color-text-normal)' }}>불러오는 중…</p>
      </div>
    );
  }

  if (notFound || !host || !program) {
    return (
      <div style={{ padding: '24px', maxWidth: '640px', margin: '0 auto', fontFamily: 'var(--font-family-primary)' }}>
        <h1 style={{ fontSize: '22px', color: 'var(--Color-text-strong)' }}>연수 페이지를 찾을 수 없습니다</h1>
        <Button variant="outline" size="medium" onClick={() => navigate(teacherTrainingHostPath(slug))}>
          허브로 돌아가기
        </Button>
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        minHeight: '100%',
        padding: '24px',
        boxSizing: 'border-box',
        fontFamily: 'var(--font-family-primary)',
        backgroundColor: 'var(--Color-background-main)',
      }}
    >
      <div style={{ maxWidth: '800px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'space-between' }}>
          <Button variant="outline" size="small" onClick={() => navigate(teacherTrainingHostPath(host.publicSlug))}>
            ← {host.displayName} 허브
          </Button>
          {isOwner && canManage && (
            <Button
              variant="primary"
              size="small"
              onClick={() => navigate(teacherTrainingProgramEditPath(program.id))}
            >
              편집
            </Button>
          )}
        </div>

        <div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
            <span
              style={{
                fontSize: '12px',
                padding: '2px 8px',
                borderRadius: '999px',
                backgroundColor: 'var(--Color-fill-hover)',
              }}
            >
              {statusLabel[program.status]}
            </span>
            {program.date && <span style={{ fontSize: '14px', color: 'var(--Color-text-normal)' }}>{program.date}</span>}
            {program.category && (
              <span style={{ fontSize: '14px', color: 'var(--Color-text-normal)' }}>{program.category}</span>
            )}
          </div>
          <h1 style={{ margin: '0 0 8px', fontSize: '28px', fontWeight: 700, color: 'var(--Color-text-strong)' }}>
            {program.title}
          </h1>
          {program.description && (
            <p style={{ margin: 0, fontSize: '16px', color: 'var(--Color-text-normal)', lineHeight: 1.6 }}>
              {program.description}
            </p>
          )}
          <p style={{ margin: '8px 0 0', fontSize: '13px', opacity: 0.7, color: 'var(--Color-text-normal)' }}>
            {host.displayName}
            {!program.isPublic && isOwner && ' · 비공개 (본인만 보기)'}
          </p>
        </div>

        <Card padding="24px" borderRadius="16px" background="var(--Color-background-normal)">
          <TrainingContentView html={program.contentHtml ?? ''} />
        </Card>
      </div>
    </div>
  );
};

export default TrainingProgramPublicPage;
